import { skipToken } from '@reduxjs/toolkit/query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { ONE, TWO } from '@/constants';
import {
  useAllSessions,
  useCurrentUser,
  useIncrementalSessions,
  useSessionsDashboard,
  useView,
} from '@/hooks';
import {
  isMomentsSession,
  SessionListType,
  SessionStatus,
  SessionWarningAction,
} from '@/types';
import { Alert, ConfirmationSnackbar, Snackbar, Typography } from '@/ui';
import {
  groupByDate,
  groupForUpcoming,
  isInNumberOfDays,
  track,
} from '@/utils';

import ClientCoaches from '../ClientCoaches';
import SessionList from '../List';
import MatchingCoach from '../MatchingCoach';
import MeetCoach from '../MeetCoach';
import SessionsTabs from '../Tabs';

import {
  deriveUpcomingViewScope,
  type UpcomingViewScope,
} from './useUpcomingViewScope';

import type { ParticipantAppointmentOperation } from '@/features/appointments/types';
import type {
  BookingConfirmationCandidate,
  StoredBookingAttempt,
} from '@/pages/MemberBiweeklyBooking/bookingAttemptState';
import type { StoredChemistryBookingAttempt } from '@/pages/MemberChemistryBooking/bookingAttemptState';
import type { Session } from '@/types';
import type { FC } from 'react';

import CancelSession from '@/components/Cal/cancelSession';
import RescheduleSession from '@/components/Cal/rescheduleSession';
import SessionWarning from '@/components/Session/SessionWarning';
import { canRenderNativeJoin } from '@/features/appointments/appointmentActionSession';
import {
  appointmentActionKindForCommand,
  appointmentRejectionMessage,
} from '@/features/appointments/appointmentStatusCopy';
import { resolveBiweeklyCoachVisibility } from '@/features/appointments/biweeklyCoachVisibility';
import { EXACT_APPOINTMENT_CONTRACT } from '@/features/appointments/browserCapability';
import { useMemberScopeAppointmentRows } from '@/features/appointments/useMemberScopeAppointmentRows';
import { useNativeAppointmentRows } from '@/features/appointments/useNativeAppointmentRows';
import { useSessionActions } from '@/features/coachSchedule/useSessionActions';
import {
  clearPendingBookingAttempt,
  confirmationCandidateFromStoredAttemptDetails,
  confirmationMatchesStoredCandidate,
  findViewerPendingBookingAttempt,
  loadPendingBookingAttemptByOperationId,
} from '@/pages/MemberBiweeklyBooking/bookingAttemptState';
import {
  clearChemistryBookingAttempt,
  clearConfirmedChemistryAttempt,
  confirmedChemistryAttemptMatchesDetails,
  findViewerPendingChemistryAttempt,
} from '@/pages/MemberChemistryBooking/bookingAttemptState';
import {
  useGetAppointmentOperationQuery,
  useLazyGetAppointmentQuery,
} from '@/store/api/appointmentApi';
import {
  useGetMomentsParticipantAppointmentOperationQuery,
  useLazyGetMomentsParticipantAppointmentQuery,
} from '@/store/api/momentsParticipantAppointmentApi';
import { useGetSessionCountQuery } from '@/store/api/sessionApi';
import { isAdmin, isCoach } from '@/types/user';

// Bounded retry cadence for fetching the confirmed appointment details after a
// followed booking operation reaches a confirmed terminal state. The interval
// keeps the follower converging on the fresh confirmed rows; the cap prevents
// unbounded refetches when the details never converge (the durable pending
// attempt is preserved for a later mount to resume in that case).
const CONFIRMATION_DETAIL_RETRY_MS = 5000;
const MAX_CONFIRMATION_DETAIL_RETRIES = 12;

const participantOperationRejection = (
  operation: ParticipantAppointmentOperation,
) => {
  if (operation.error) {
    return {
      code: operation.error.error,
      message: operation.error.error,
    };
  }
  if (operation.status === 'needs_reconciliation') {
    return {
      code: 'operation_needs_reconciliation',
      message: 'The appointment operation requires manual review.',
    };
  }
  return null;
};

// Full "MonthName YYYY" label → sortable numeric anchor (millis at the
// first-of-month, UTC). Deterministic across engines because it does not
// rely on `Date.parse` of a non-spec-guaranteed string. Returns null when
// the label does not match the expected shape so the caller can fall back
// to a stable label comparison.
const MONTH_INDEX: Readonly<Record<string, number>> = {
  January: 0,
  February: 1,
  March: 2,
  April: 3,
  May: 4,
  June: 5,
  July: 6,
  August: 7,
  September: 8,
  October: 9,
  November: 10,
  December: 11,
};

const monthLabelAnchor = (label: string): number | null => {
  const [monthName, yearString] = label.split(' ');
  if (!monthName || !yearString) return null;
  const monthIndex = MONTH_INDEX[monthName];
  if (monthIndex === undefined) return null;
  const year = Number.parseInt(yearString, 10);
  if (!Number.isFinite(year)) return null;
  return Date.UTC(year, monthIndex, 1);
};

// Sort every bucket ascending by absolute instant and order month keys
// by the parsed month-label anchor. Runs unconditionally on the merge
// output so a Member with zero native appointments still sees a
// deterministic display order (previously the memo's early-return case
// skipped this and rendered whatever iteration order `groupByDate`
// produced — Rails' insertion order, which is not guaranteed to be
// chronological).
const sortMergedByMonth = (
  merged: Record<string, Session[]>,
): Record<string, Session[]> => {
  Object.values(merged).forEach((bucket) => {
    bucket.sort(
      (left, right) => Date.parse(left.startedAt) - Date.parse(right.startedAt),
    );
  });
  return Object.fromEntries(
    Object.entries(merged).sort(([leftMonth], [rightMonth]) => {
      const leftAnchor = monthLabelAnchor(leftMonth);
      const rightAnchor = monthLabelAnchor(rightMonth);
      if (leftAnchor === null || rightAnchor === null) {
        return leftMonth.localeCompare(rightMonth);
      }
      return leftAnchor - rightAnchor;
    }),
  );
};

// Length-checks that tolerate either the record shape returned by the real
// `groupForUpcoming` or an array shape a test mock might pass in.
const isEmptyGrouping = (value: unknown): boolean => {
  if (Array.isArray(value)) return value.length === 0;
  if (value && typeof value === 'object') {
    return Object.values(value as Record<string, unknown[]>).every(
      (list) => list.length === 0,
    );
  }
  return true;
};

interface ScopedUpcomingProps {
  scope: UpcomingViewScope;
}

interface UpcomingRouteState {
  pendingAppointmentCoachName?: string;
  pendingAppointmentOperationId?: string;
  sessionCancelled?: boolean;
}

const ScopedUpcoming: FC<ScopedUpcomingProps> = ({ scope }) => {
  const {
    actingRole,
    isOverview,
    legacyUserId,
    nativeSource,
    viewer: user,
  } = scope;
  const location = useLocation();
  const navigate = useNavigate();
  const isMemberViewer = !isOverview && actingRole !== 'coach';
  const skipSessionCount =
    !isMemberViewer || !user.id || user.coaches.length === 0;
  const {
    data: sessionCounts = [],
    isLoading: isSessionCountLoading,
    isSuccess: isSessionCountSuccess,
  } = useGetSessionCountQuery(user.id, { skip: skipSessionCount });
  const coachesWithSessions = new Set(
    sessionCounts.map(([coachId]) => coachId),
  );
  // Skipped for a coach, admin, or support viewer: /sessions/dashboard is
  // member-scoped. A skipped query stays uninitialised, which the visibility
  // resolver reads as non-authoritative, so those viewers never narrow.
  const {
    data: sessionsDashboard,
    isError: isSessionsDashboardError,
    isFetching: isSessionsDashboardFetching,
    isSuccess: isSessionsDashboardSuccess,
  } = useSessionsDashboard({ skip: !isMemberViewer });
  const routeState = location.state as UpcomingRouteState | null;
  const pendingAppointmentCoachName =
    routeState?.pendingAppointmentCoachName?.trim();
  const pendingAppointmentOperationIdFromRoute =
    routeState?.pendingAppointmentOperationId?.trim();
  // Resume the member's own admitted booking directly from the persisted
  // attempt, so an escaped pending booking confirms on Upcoming even without
  // router state. Router state only supplies the initial coach copy.
  const storedFollowedAttempt = useMemo(() => {
    if (!isMemberViewer) return null;
    const byRoute = pendingAppointmentOperationIdFromRoute
      ? loadPendingBookingAttemptByOperationId(
          user.id,
          pendingAppointmentOperationIdFromRoute,
        )
      : null;
    return byRoute ?? findViewerPendingBookingAttempt(user.id);
  }, [isMemberViewer, pendingAppointmentOperationIdFromRoute, user.id]);
  // MEN-4915 (PR #1664 review): chemistry bookings confirm as soon as the
  // admission response lands, so a member can leave while the operation is
  // still pending. Nothing else observes chemistry attempts, so adopt one here
  // when no bi-weekly attempt is being followed. Only the operation id is
  // required -- the confirmed branch below already falls back to refresh-only
  // behaviour when there is no stored series candidate.
  const followedChemistryAttempt = useMemo(() => {
    if (!isMemberViewer || storedFollowedAttempt) return null;
    return findViewerPendingChemistryAttempt(user.id);
  }, [isMemberViewer, storedFollowedAttempt, user.id]);
  const followedChemistryOwnerKey = followedChemistryAttempt?.ownerKey ?? null;
  const pendingConfirmationCoachName =
    pendingAppointmentCoachName ??
    (storedFollowedAttempt
      ? (storedFollowedAttempt.confirmationCandidate?.coach.displayName ??
        'your coach')
      : null) ??
    (followedChemistryAttempt
      ? followedChemistryAttempt.presentation.coachFullName || 'your coach'
      : null);
  const pendingAppointmentMessageFromRoute = pendingConfirmationCoachName
    ? `We're confirming your sessions with ${pendingConfirmationCoachName}. This can take a few minutes — we'll email you as soon as they're set, and they'll show up here automatically.`
    : null;
  const cancellationConfirmedFromRoute = Boolean(
    routeState?.sessionCancelled && !pendingAppointmentMessageFromRoute,
  );
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(
    cancellationConfirmedFromRoute,
  );
  const [pendingAppointmentMessage, setPendingAppointmentMessage] = useState(
    pendingAppointmentMessageFromRoute,
  );
  const [
    pendingAppointmentRejectionMessage,
    setPendingAppointmentRejectionMessage,
  ] = useState<string | null>(null);
  const [
    pendingAppointmentReconciliationMessage,
    setPendingAppointmentReconciliationMessage,
  ] = useState<string | null>(null);
  // Holds a confirmed operation whose admitted appointment details must be
  // validated against the stored candidate once the refreshed native list
  // contains them. Validation is deferred so it runs on the post-confirmation
  // details, not the stale pre-confirmation cache.
  const [confirmationValidation, setConfirmationValidation] = useState<{
    appointmentIds: string[];
    candidate: BookingConfirmationCandidate;
    routeKey: string | null;
  } | null>(null);
  const [directConfirmationValidation, setDirectConfirmationValidation] =
    useState<{
      appointmentIds: string[];
      attempt: number;
      routeKey: string | null;
      storedAttempt: StoredBookingAttempt;
    } | null>(null);
  const [chemistryConfirmationValidation, setChemistryConfirmationValidation] =
    useState<{
      appointmentIds: string[];
      attempt: number;
      storedAttempt: StoredChemistryBookingAttempt;
    } | null>(null);
  // Bounds how many times the post-terminal confirmed-details fetch is retried
  // for the current followed operation, so a persistently stale native page
  // cannot drive unbounded refetches.
  const confirmationRetryCount = useRef(0);
  const [pendingAppointmentOperationId, setPendingAppointmentOperationId] =
    useState(
      pendingAppointmentOperationIdFromRoute ??
        storedFollowedAttempt?.pendingOperationId ??
        followedChemistryAttempt?.operationId ??
        undefined,
    );
  const followedRouteKey = storedFollowedAttempt?.routeKey ?? null;
  const followedCandidate =
    storedFollowedAttempt?.confirmationCandidate ?? null;
  // The paired v1 mutation response cannot carry `retry_after_seconds`, so the
  // stored hint may be absent. The live pending operation response is the
  // authoritative source for the poll cadence; seed from the stored hint and
  // update from each pending operation response below.
  const [liveRetryAfterSeconds, setLiveRetryAfterSeconds] = useState<
    number | null
  >(
    storedFollowedAttempt?.retryAfterSeconds ??
      followedChemistryAttempt?.retryAfterSeconds ??
      null,
  );
  const pollingInterval = pendingAppointmentOperationId
    ? Math.max(1000, (liveRetryAfterSeconds ?? 5) * 1000)
    : 0;
  const followedTransport =
    storedFollowedAttempt?.transport ??
    followedChemistryAttempt?.transport ??
    'rails';
  const railsPendingAppointmentOperation = useGetAppointmentOperationQuery(
    pendingAppointmentOperationId && followedTransport === 'rails'
      ? {
          browserContractMarker: EXACT_APPOINTMENT_CONTRACT,
          operationId: pendingAppointmentOperationId,
        }
      : skipToken,
    { pollingInterval },
  );
  const momentsPendingAppointmentOperation =
    useGetMomentsParticipantAppointmentOperationQuery(
      pendingAppointmentOperationId && followedTransport === 'moments'
        ? { operationId: pendingAppointmentOperationId }
        : skipToken,
      { pollingInterval },
    );
  const [getMomentsAppointment] =
    useLazyGetMomentsParticipantAppointmentQuery();
  const [getAppointment] = useLazyGetAppointmentQuery();
  const pendingAppointmentOperation =
    followedTransport === 'moments'
      ? {
          ...momentsPendingAppointmentOperation,
          currentData: momentsPendingAppointmentOperation.currentData
            ? {
                ...momentsPendingAppointmentOperation.currentData,
                rejection: participantOperationRejection(
                  momentsPendingAppointmentOperation.currentData,
                ),
              }
            : undefined,
        }
      : railsPendingAppointmentOperation;

  // Adopt the server-directed retry interval (including shorter hints) from the
  // live pending operation response.
  useEffect(() => {
    const operation = pendingAppointmentOperation.currentData;
    if (
      !pendingAppointmentOperationId ||
      !operation ||
      operation.id !== pendingAppointmentOperationId ||
      operation.status !== 'pending'
    ) {
      return;
    }
    if (operation.retryAfterSeconds !== liveRetryAfterSeconds) {
      setLiveRetryAfterSeconds(operation.retryAfterSeconds);
    }
  }, [
    liveRetryAfterSeconds,
    pendingAppointmentOperation.currentData,
    pendingAppointmentOperationId,
  ]);

  useEffect(() => {
    if (!cancellationConfirmedFromRoute && !pendingAppointmentMessageFromRoute)
      return;
    navigate('.', {
      replace: true,
      state: pendingAppointmentOperationIdFromRoute
        ? {
            pendingAppointmentOperationId:
              pendingAppointmentOperationIdFromRoute,
          }
        : null,
    });
  }, [
    cancellationConfirmedFromRoute,
    navigate,
    pendingAppointmentMessageFromRoute,
    pendingAppointmentOperationIdFromRoute,
  ]);

  // MEN-4928: no pagination control, so the list must be every page the
  // server has — including a coach viewer, whose schedule is the largest.
  const {
    data: sessions = {
      items: {} as Record<string, Session[]>,
      pages: ONE,
      total: ONE,
    },
    isLoading,
    refetch,
  } = useAllSessions(
    {
      statuses: [SessionStatus.ACTIVE, SessionStatus.RESCHEDULED],
      actingRole,
      userId: legacyUserId,
    },
    'always',
  );
  // Native Moments Appointments — the same authoritative surface the
  // /m/appointments page uses, merged into the legacy Sessions timeline so
  // a Member who books a native Series sees it interleaved chronologically
  // with any legacy Cal sessions rather than staring at "No upcoming
  // sessions".
  //
  // Participant rows are scoped to Members or Coaches viewing their own
  // sessions. The distinct admin projection is scoped to the existing
  // coach-viewing-member overview.
  //
  // The hook owns the query, the ticking clock, the booking / lifecycle /
  // endAt filter, the (startAt, id) sort, and the `appointmentActionSession`
  // adapter. The scrub below uses `nativeAppointmentsById` — which the hook
  // populates from EVERY appointment the server returned, including cancelled
  // and expired rows — plus `nowTick` for the same-clock predicate.
  const canFetchParticipantAppointments =
    nativeSource.kind === 'participant' || nativeSource.kind === 'coach';
  const canFetchMemberScopeAppointments = nativeSource.kind === 'member-scope';
  let nativeActingRole: 'member' | 'coach' | undefined;
  if (nativeSource.kind === 'coach') {
    nativeActingRole = 'coach';
  } else if (nativeSource.kind === 'participant' && isMemberViewer) {
    nativeActingRole = 'member';
  }
  const participantAppointmentRows = useNativeAppointmentRows({
    actingRole: nativeActingRole,
    enabled: canFetchParticipantAppointments,
    userId:
      nativeSource.kind === 'participant' || nativeSource.kind === 'coach'
        ? nativeSource.userId
        : '',
  });
  const memberScopeRowsReadOnly = !isCoach(user) || isAdmin(user);
  const memberScopeAppointmentRows = useMemberScopeAppointmentRows({
    coachId: memberScopeRowsReadOnly ? undefined : user.id,
    enabled: canFetchMemberScopeAppointments,
    memberId: nativeSource.kind === 'member-scope' ? nativeSource.memberId : '',
    readOnly: memberScopeRowsReadOnly,
  });
  useEffect(() => {
    const operation = pendingAppointmentOperation.currentData;
    if (
      !pendingAppointmentOperationId ||
      !operation ||
      operation.id !== pendingAppointmentOperationId ||
      operation.status === 'pending'
    ) {
      return;
    }
    // Stop polling on any terminal result. Router state is cleared for legacy
    // handoffs.
    setPendingAppointmentOperationId(undefined);
    setPendingAppointmentMessage(null);
    if (pendingAppointmentOperationIdFromRoute) {
      navigate('.', { replace: true, state: null });
    }
    if (operation.status === 'confirmed') {
      if (canFetchMemberScopeAppointments) {
        memberScopeAppointmentRows.refetch();
      }
      // Fetch the confirmed appointment details, then validate them against
      // the stored candidate in a follow-up effect. Deferring avoids a false
      // reconciliation when the pre-confirmation native cache has not caught
      // up. When there is no stored candidate (legacy router-state handoff),
      // fall back to the existing refresh-only behavior.
      if (canFetchParticipantAppointments) {
        participantAppointmentRows.refetch();
      }
      refetch();
      if (followedChemistryAttempt) {
        setChemistryConfirmationValidation({
          appointmentIds: operation.appointmentIds,
          attempt: 0,
          storedAttempt: followedChemistryAttempt,
        });
      } else if (followedTransport === 'moments' && storedFollowedAttempt) {
        setDirectConfirmationValidation({
          appointmentIds: operation.appointmentIds,
          attempt: 0,
          routeKey: followedRouteKey,
          storedAttempt: storedFollowedAttempt,
        });
      } else if (followedCandidate) {
        confirmationRetryCount.current = 0;
        setConfirmationValidation({
          appointmentIds: operation.appointmentIds,
          candidate: followedCandidate,
          routeKey: followedRouteKey,
        });
      } else if (followedRouteKey) {
        clearPendingBookingAttempt(followedRouteKey);
      }
    } else {
      if (followedRouteKey) clearPendingBookingAttempt(followedRouteKey);
      if (followedChemistryOwnerKey) {
        clearChemistryBookingAttempt(followedChemistryOwnerKey);
      }
      setPendingAppointmentRejectionMessage(
        appointmentRejectionMessage(
          operation.rejection,
          appointmentActionKindForCommand(operation.command),
        ),
      );
    }
  }, [
    canFetchMemberScopeAppointments,
    canFetchParticipantAppointments,
    followedCandidate,
    followedChemistryAttempt,
    followedChemistryOwnerKey,
    followedRouteKey,
    followedTransport,
    getMomentsAppointment,
    memberScopeAppointmentRows,
    navigate,
    participantAppointmentRows,
    pendingAppointmentOperation.currentData,
    pendingAppointmentOperationId,
    pendingAppointmentOperationIdFromRoute,
    refetch,
  ]);

  useEffect(() => {
    if (!chemistryConfirmationValidation) return undefined;
    const { appointmentIds, attempt, storedAttempt } =
      chemistryConfirmationValidation;
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    const retryOrReconcile = () => {
      if (cancelled) return;
      const nextAttempt = attempt + 1;
      if (nextAttempt >= MAX_CONFIRMATION_DETAIL_RETRIES) {
        setChemistryConfirmationValidation(null);
        setPendingAppointmentReconciliationMessage(
          'We could not automatically confirm this session. Refresh to check your schedule or contact support before booking again.',
        );
        return;
      }
      retryTimer = setTimeout(() => {
        setChemistryConfirmationValidation({
          appointmentIds,
          attempt: nextAttempt,
          storedAttempt,
        });
      }, CONFIRMATION_DETAIL_RETRY_MS);
    };
    const detailRequests = appointmentIds.map((appointmentId) =>
      storedAttempt.transport === 'moments'
        ? getMomentsAppointment({ appointmentId })
        : getAppointment({ appointmentId }),
    );
    Promise.all(detailRequests.map((request) => request.unwrap()))
      .then((appointments) => {
        if (cancelled) return;
        if (
          !confirmedChemistryAttemptMatchesDetails({
            appointmentIds,
            appointments,
            attempt: storedAttempt,
          })
        ) {
          retryOrReconcile();
          return;
        }
        const clearResult = clearConfirmedChemistryAttempt({
          idempotencyKey: storedAttempt.idempotencyKey,
          ownerKey: storedAttempt.ownerKey,
        });
        if (clearResult !== 'cleared') {
          setPendingAppointmentReconciliationMessage(
            'We could not safely finish confirming this session. Refresh to check your schedule or contact support.',
          );
          return;
        }
        setChemistryConfirmationValidation(null);
      })
      .catch(retryOrReconcile)
      .finally(() => {
        detailRequests.forEach((request) => request.unsubscribe());
      });
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [chemistryConfirmationValidation, getAppointment, getMomentsAppointment]);

  useEffect(() => {
    if (!directConfirmationValidation) return undefined;
    const { appointmentIds, attempt, routeKey, storedAttempt } =
      directConfirmationValidation;
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    const retryOrReconcile = () => {
      if (cancelled) return;
      const nextAttempt = attempt + 1;
      if (nextAttempt >= MAX_CONFIRMATION_DETAIL_RETRIES) {
        setDirectConfirmationValidation(null);
        setPendingAppointmentReconciliationMessage(
          'We could not automatically confirm these sessions. Refresh to check your schedule or contact support.',
        );
        return;
      }
      retryTimer = setTimeout(() => {
        setDirectConfirmationValidation({
          appointmentIds,
          attempt: nextAttempt,
          routeKey,
          storedAttempt,
        });
      }, CONFIRMATION_DETAIL_RETRY_MS);
    };
    const detailRequests = appointmentIds.map((appointmentId) =>
      getMomentsAppointment({ appointmentId }),
    );
    Promise.all(detailRequests.map((request) => request.unwrap()))
      .then((appointments) => {
        if (cancelled) return;
        const allAppointmentsConfirmed = appointments.every(
          (appointment) => appointment.bookingStatus === 'confirmed',
        );
        const candidate = storedAttempt.confirmationCandidate;
        const detailsMatch = candidate
          ? confirmationMatchesStoredCandidate(candidate, appointments)
          : confirmationCandidateFromStoredAttemptDetails({
              appointments,
              attempt: storedAttempt,
              memberEmail: user.email,
            }) !== null;
        if (!allAppointmentsConfirmed || !detailsMatch) {
          retryOrReconcile();
          return;
        }
        if (routeKey) clearPendingBookingAttempt(routeKey);
        setDirectConfirmationValidation(null);
      })
      .catch(retryOrReconcile)
      .finally(() => {
        detailRequests.forEach((request) => request.unsubscribe());
      });
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [directConfirmationValidation, getMomentsAppointment]);

  // Validate a confirmed booking once the refreshed native appointment details
  // are available (identity, series, count, cadence, schedules). Consumes the
  // followed attempt exactly once; a genuine mismatch shows reconciliation copy
  // rather than synthesizing success.
  useEffect(() => {
    if (!confirmationValidation) return;
    const { appointmentIds, candidate, routeKey } = confirmationValidation;
    const idsMatchCandidate =
      appointmentIds.length === candidate.appointmentIds.length &&
      appointmentIds.every((id) => candidate.appointmentIds.includes(id));
    // Only accept appointments the post-terminal fetch has resolved to
    // `confirmed`. The `nativeAppointmentsById` map also carries pre-confirmation
    // snapshots (e.g. a background-admission `pending` row cached under the same
    // id), which must not be mistaken for the fresh confirmed details.
    const confirmedAppointments = appointmentIds
      .map((id) => participantAppointmentRows.nativeAppointmentsById.get(id))
      .filter(
        (appointment): appointment is NonNullable<typeof appointment> =>
          appointment !== undefined &&
          appointment.bookingStatus === 'confirmed',
      );
    // An operation whose appointment id set differs from the stored candidate
    // is an immediate mismatch; its details will never arrive. Otherwise wait
    // until every admitted appointment is present AND confirmed in the refreshed
    // list before validating the deeper identity/series/count/cadence/schedule
    // fields and consuming the durable pending attempt. Consumption is gated on
    // confirmed state so a stale pending snapshot cannot consume the attempt.
    // The bounded retry scheduler below keeps re-fetching until the confirmed
    // rows arrive; it must not latch on the fetched map identity, because a
    // successful refetch can return the same deeply-equal stale pending page.
    if (
      idsMatchCandidate &&
      confirmedAppointments.length !== appointmentIds.length
    ) {
      return;
    }
    if (routeKey) clearPendingBookingAttempt(routeKey);
    setConfirmationValidation(null);
    if (
      !idsMatchCandidate ||
      !confirmationMatchesStoredCandidate(candidate, confirmedAppointments)
    ) {
      setPendingAppointmentReconciliationMessage(
        'We could not automatically confirm these sessions. Refresh to check your schedule or contact support.',
      );
    }
  }, [confirmationValidation, participantAppointmentRows]);

  // Bounded retry cadence for the post-terminal confirmed details. While a
  // confirmed operation is awaiting its confirmed appointment rows, re-fetch on
  // a fixed schedule until they arrive, then stop. This does not depend on the
  // fetched payload identity, so a successful-but-stale pending page (RTK Query
  // structurally shares deeply-equal payloads) cannot latch retries off.
  useEffect(() => {
    if (!confirmationValidation || !canFetchParticipantAppointments) {
      return undefined;
    }
    const { appointmentIds, candidate } = confirmationValidation;
    const idsMatchCandidate =
      appointmentIds.length === candidate.appointmentIds.length &&
      appointmentIds.every((id) => candidate.appointmentIds.includes(id));
    // A differing id set is resolved immediately by the validation effect; no
    // retry is needed.
    if (!idsMatchCandidate) return undefined;
    const confirmedPresent = appointmentIds.every(
      (id) =>
        participantAppointmentRows.nativeAppointmentsById.get(id)
          ?.bookingStatus === 'confirmed',
    );
    if (confirmedPresent) return undefined;
    if (confirmationRetryCount.current >= MAX_CONFIRMATION_DETAIL_RETRIES) {
      return undefined;
    }
    const timer = setTimeout(() => {
      confirmationRetryCount.current += 1;
      participantAppointmentRows.refetch();
    }, CONFIRMATION_DETAIL_RETRY_MS);
    return () => clearTimeout(timer);
  }, [
    canFetchParticipantAppointments,
    confirmationValidation,
    participantAppointmentRows,
  ]);
  const canUseMemberScopeReconciliation =
    canFetchMemberScopeAppointments &&
    !memberScopeAppointmentRows.isUnavailable &&
    (!memberScopeAppointmentRows.isError ||
      memberScopeAppointmentRows.adminAppointmentsById.size > 0);
  const canFetchNativeAppointments =
    canFetchParticipantAppointments || canUseMemberScopeReconciliation;
  const shouldScrubUnreconciledAppointmentRows =
    canFetchNativeAppointments ||
    (canFetchMemberScopeAppointments && memberScopeRowsReadOnly);
  const nativeSessions = canUseMemberScopeReconciliation
    ? memberScopeAppointmentRows.adminSessions
    : participantAppointmentRows.nativeSessions;
  const nativeAppointmentsById = canUseMemberScopeReconciliation
    ? memberScopeAppointmentRows.adminAppointmentsById
    : participantAppointmentRows.nativeAppointmentsById;
  const nowTick = canUseMemberScopeReconciliation
    ? memberScopeAppointmentRows.nowTick
    : participantAppointmentRows.nowTick;
  const nativeIsLoading = canFetchMemberScopeAppointments
    ? memberScopeAppointmentRows.isLoading
    : participantAppointmentRows.isLoading;
  const nativeIsError = canFetchMemberScopeAppointments
    ? memberScopeAppointmentRows.isError
    : participantAppointmentRows.isError;
  const nativeStatusApplies =
    canFetchNativeAppointments || canFetchMemberScopeAppointments;
  const nativeErrorBannerCopy = canFetchMemberScopeAppointments
    ? "We couldn't load this member's Mento appointments right now. Any sessions above are shown from the member's existing schedule."
    : "We couldn't load your Mento appointments right now. Any sessions above are shown from your existing schedule.";
  const nativeIsSuccess = canFetchMemberScopeAppointments
    ? memberScopeAppointmentRows.isSuccess
    : participantAppointmentRows.isSuccess;
  const hasResolvedNativeAppointments =
    !canFetchNativeAppointments || nativeIsSuccess;
  const hasScheduledNativeChemistry = Array.from(
    participantAppointmentRows.nativeAppointmentsById.values(),
  ).some(
    (appointment) =>
      appointment.bookingStatus === 'confirmed' &&
      appointment.kind === 'chemistry' &&
      appointment.lifecycleStatus !== 'cancelled',
  );
  const isMatchedStage =
    !skipSessionCount &&
    isSessionCountSuccess &&
    hasResolvedNativeAppointments &&
    !nativeIsLoading &&
    !nativeIsError &&
    !hasScheduledNativeChemistry &&
    !user.coaches.some((coach) => coachesWithSessions.has(coach.id));
  // The merge does two independent things:
  //
  //   1. Scrub + enrich kept legacy rows against their paired native, so a
  //      legacy Session projection with a stale HTTPS `location` or missing
  //      cancel/reschedule capability flags is corrected before it renders.
  //      Without this reconciliation a legacy row would keep firing
  //      SessionCard's Join CTA and offering Cancel/Reschedule for actions
  //      the native API would reject.
  //
  //   2. Interleave native rows into the legacy month buckets. Both lists
  //      are complete authoritative sets — the timeline is a single long
  //      scroll (MEN-4928), so every row is merged exactly once.
  //
  // Same-appointment reconciliation follows `buildAppointmentTimelineItems`:
  // if any legacy Session carries `sourceAppointmentId` matching a native
  // and its `sourceAppointmentVersion` >= the native's version, keep the
  // Session and drop the native (identical or older snapshot); if the
  // native is newer OR the native says the appointment is cancelled, drop
  // the Session and render the native instead. That preserves the mixed-
  // projection state without ever double-rendering the same appointment,
  // and hides a legacy row for an appointment the native has cancelled
  // even if the legacy projection is at a version >= the native.
  const mergedSessionsByMonth = useMemo(() => {
    const makeProjectionReadOnly = (session: Session): Session => ({
      ...session,
      location: undefined,
      sourceAppointmentCancelAllowed: false,
      sourceAppointmentRescheduleAllowed: false,
    });
    const grouped = groupByDate(sessions.items);
    if (nativeAppointmentsById.size === 0) {
      // No natives to reconcile against; still normalise the display
      // order so the zero-native path agrees with the merged path's sort
      // semantics. `groupByDate` shares bucket references with
      // `sessions.items` in this mode, so build a shallow clone before
      // sorting to avoid mutating the RTK cache in place. Appointment-backed
      // projections remain visible, but their actions stay read-only until
      // their own authoritative appointment reconciles them.
      const cloned: Record<string, Session[]> = {};
      Object.entries(grouped).forEach(([month, bucket]) => {
        cloned[month] = bucket.map((session) =>
          shouldScrubUnreconciledAppointmentRows && session.sourceAppointmentId
            ? makeProjectionReadOnly(session)
            : session,
        );
      });
      return sortMergedByMonth(cloned);
    }

    const legacySessionVersionsByAppointmentId = new Map<string, number>();
    Object.values(grouped).forEach((bucket) => {
      bucket.forEach((session) => {
        if (
          !session.sourceAppointmentId ||
          session.sourceAppointmentVersion === null ||
          session.sourceAppointmentVersion === undefined
        )
          return;
        const previous = legacySessionVersionsByAppointmentId.get(
          session.sourceAppointmentId,
        );
        if (
          previous === undefined ||
          session.sourceAppointmentVersion > previous
        ) {
          legacySessionVersionsByAppointmentId.set(
            session.sourceAppointmentId,
            session.sourceAppointmentVersion,
          );
        }
      });
    });
    // Compute suppression from the RAW appointment set, not from the
    // render-eligible `nativeSessions`. `nativeSessions` has already
    // filtered out cancelled + expired appointments; if we iterated only
    // it, a newer cancelled native (version 6) paired with an older legacy
    // projection (version 5) would leave the legacy row visible under
    // Upcoming because the cancelled native never entered this loop to
    // register a suppression. Iterating the raw map matches how
    // `buildAppointmentTimelineItems` reconciles the same-appointment case.
    //
    // Rules for suppressing a paired legacy row:
    //   - Native is at a strictly higher version than legacy → suppress
    //     (native replaces legacy; the native is added below).
    //   - Native is cancelled at ANY version, including the same version
    //     as legacy → suppress. If the native side says the appointment is
    //     cancelled, the legacy row is a stale projection of a dead
    //     appointment; showing it (with Cancel/Reschedule) would let the
    //     user act on a lifecycle the server will reject.
    //   - Native is at a lower or equal version and NOT cancelled → keep
    //     legacy. Enrichment / location scrub below still runs so any
    //     capability transitions on the native reach the kept row.
    //   - Legacy has no known version → treat as unversioned and suppress
    //     (fall through the `!== undefined` check).
    const suppressedLegacyAppointmentIds = new Set<string>();
    nativeAppointmentsById.forEach((appointment, appointmentId) => {
      const legacyVersion =
        legacySessionVersionsByAppointmentId.get(appointmentId);
      const isCancelledNative = appointment.lifecycleStatus === 'cancelled';
      const legacyIsAtLeastAsFreshAsNative =
        legacyVersion !== undefined && appointment.version <= legacyVersion;
      if (legacyIsAtLeastAsFreshAsNative && !isCancelledNative) return;
      suppressedLegacyAppointmentIds.add(appointmentId);
    });
    const eligibleNatives = nativeSessions.filter((native) => {
      if (!native.sourceAppointmentId) return true;
      // A native survives when it is render-eligible AND its legacy
      // projection was suppressed above (native is newer or the projection
      // is missing). If the legacy projection was fresher, the native has
      // no matching entry in `suppressedLegacyAppointmentIds` and drops
      // out here to avoid a duplicate render.
      return suppressedLegacyAppointmentIds.has(native.sourceAppointmentId);
    });
    // Set of appointment ids that will render as a native row in the
    // timeline. Used below to suppress a stale legacy projection whose
    // newer native is already rendered — without this the same appointment
    // would appear twice, once as the fresh native and once as the stale
    // legacy projection.
    const renderedNativeAppointmentIds = new Set<string>();
    eligibleNatives.forEach((native) => {
      if (native.sourceAppointmentId) {
        renderedNativeAppointmentIds.add(native.sourceAppointmentId);
      }
    });

    // Drop a legacy row only when its paired native actually "covers" the
    // appointment. Coverage means either the native is render-eligible
    // (would appear as a native row) OR the native is authoritatively
    // cancelled (the appointment is dead — the legacy row must not surface
    // Cancel/Reschedule for a lifecycle the server has closed).
    //
    // An unconditional drop would silently remove the row for the case
    // where the native suppressed the legacy on version alone but was
    // itself filtered out of render-eligibility (expired past endAt,
    // dropped by the identity guard, `bookingStatus !== 'confirmed'`,
    // etc.).
    const shouldDropSuppressedLegacy = (sourceAppointmentId: string) => {
      const native = nativeAppointmentsById.get(sourceAppointmentId);
      if (!native) return false;
      if (native.lifecycleStatus === 'cancelled') return true;
      return renderedNativeAppointmentIds.has(sourceAppointmentId);
    };

    const merged: Record<string, Session[]> = {};
    Object.entries(grouped).forEach(([month, bucket]) => {
      const kept = bucket
        .filter((session) => {
          if (!session.sourceAppointmentId) return true;
          if (!suppressedLegacyAppointmentIds.has(session.sourceAppointmentId))
            return true;
          return !shouldDropSuppressedLegacy(session.sourceAppointmentId);
        })
        .map((session) => {
          // Reconciliation kept the legacy Session projection over the
          // native. Two things can still be stale on the legacy row and
          // must be reconciled against the paired native before render:
          //
          //   1. Cancel/reschedule capability fields — a legacy projection
          //      that pre-dates native adaptation carries these as
          //      undefined, and even a native-aware projection can lag
          //      the appointment's capability transitions. Mirrors the
          //      enrichment SessionTimeline does at build time so
          //      SessionCard menus never show an action the native API
          //      would reject.
          //
          //   2. `location` — taken from the native join link, so SessionCard's
          //      Join CTA opens the Google event's room and disappears
          //      whenever canRenderNativeJoin is false against `nowTick`.
          //
          // Both reconciliations run on every kept legacy row so it gets
          // the same authoritative capability set the /sessions/upcoming
          // timeline reports.
          if (!session.sourceAppointmentId) return session;
          const native = nativeAppointmentsById.get(
            session.sourceAppointmentId,
          );
          if (!native) {
            return shouldScrubUnreconciledAppointmentRows
              ? makeProjectionReadOnly(session)
              : session;
          }

          // The "cancelled paired native" branch is handled entirely by
          // the suppression filter above — any legacy row paired with a
          // cancelled native has already been dropped (a cancelled native
          // always covers the appointment per `shouldDropSuppressedLegacy`).
          // At this point every kept legacy row has a paired native whose
          // lifecycle is `scheduled`, `reschedule_pending`, or
          // `cancellation_pending`.
          //
          // Only touch capability/location fields when the native is at
          // least as fresh as the legacy — otherwise the legacy is the
          // authoritative snapshot and its own fields must win. An older
          // native's stale `join.allowed` cannot be trusted to wipe a
          // valid location on a fresher legacy row (the previous asymmetric
          // rule ran the location scrub even in the legacy-fresher branch,
          // which could drop a valid Join CTA).
          const nativeIsAtLeastAsFreshAsLegacy =
            session.sourceAppointmentVersion === null ||
            session.sourceAppointmentVersion === undefined ||
            native.version >= session.sourceAppointmentVersion;
          if (!nativeIsAtLeastAsFreshAsLegacy) return session;

          let enriched: Session = {
            ...session,
            sourceAppointmentCancelAllowed:
              native.actionCapabilities.cancel.allowed &&
              native.actionCapabilities.cancel.scopes.includes('appointment'),
            sourceAppointmentCancelReasonRequired:
              native.actionCapabilities.cancel.reasonRequired,
            sourceAppointmentCancelScopes:
              native.actionCapabilities.cancel.scopes,
            sourceAppointmentRescheduleAllowed:
              native.actionCapabilities.reschedule.allowed &&
              native.actionCapabilities.reschedule.scopes.includes(
                'appointment',
              ),
            sourceAppointmentSeriesId: native.seriesId,
            sourceAppointmentSeriesMutationVersion:
              native.seriesMutationVersion,
            sourceAppointmentVersion: native.version,
          };

          // MEN-5454: the native join link is authoritative. A rebound
          // session's Rails location (withheld by Rails, or a stale Cal-era
          // Meet URL) is never the room on the Google event.
          enriched = {
            ...enriched,
            location: canRenderNativeJoin(native, nowTick)
              ? (native.meeting.joinUrl ?? undefined)
              : undefined,
          };

          return enriched;
        });
      if (kept.length) merged[month] = kept;
    });

    eligibleNatives.forEach((session) => {
      // Native month key uses the same "en-US" long-month formula
      // groupByDate emits for its keys, so a native at 2026-08-05 lands
      // in the same "August 2026" bucket a legacy session at 2026-08-15
      // did. Both formulas use the viewer's default runtime timezone;
      // do not pass `timeZone` here — that would introduce a per-native
      // tz that disagrees with the viewer-local rule groupByDate uses.
      const month = new Date(session.startedAt).toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric',
      });
      merged[month] = [...(merged[month] ?? []), session];
    });

    return sortMergedByMonth(merged);
  }, [
    canFetchNativeAppointments,
    nativeAppointmentsById,
    nativeSessions,
    nowTick,
    sessions.items,
    shouldScrubUnreconciledAppointmentRows,
  ]);

  // Once the member holds an active Moments biweekly, the page narrows to that
  // coaching relationship: the other matched coaches lose their cards in
  // `ClientCoaches` and their sessions leave the timeline here, so the two
  // sections never disagree about which coaches the member is working with.
  const { isNarrowed, isVisibleSession } = useMemo(
    () =>
      resolveBiweeklyCoachVisibility({
        activeBiweekly: sessionsDashboard?.activeBiweekly,
        coachIds: user.coaches.map((coach) => coach.id),
        dashboardQuery: {
          hasCurrentData: !!sessionsDashboard,
          isError: isSessionsDashboardError,
          isFetching: isSessionsDashboardFetching,
          isSuccess: isSessionsDashboardSuccess,
        },
        sessions: nativeSessions,
      }),
    [
      isSessionsDashboardError,
      isSessionsDashboardFetching,
      isSessionsDashboardSuccess,
      nativeSessions,
      sessionsDashboard,
      user.coaches,
    ],
  );
  const visibleSessionsByMonth = useMemo(() => {
    if (!isNarrowed) return mergedSessionsByMonth;
    const visible: Record<string, Session[]> = {};
    Object.entries(mergedSessionsByMonth).forEach(([month, bucket]) => {
      const kept = bucket.filter(isVisibleSession);
      if (kept.length) visible[month] = kept;
    });
    return visible;
  }, [isNarrowed, isVisibleSession, mergedSessionsByMonth]);
  // `groupForUpcoming` lifts the first row into a "Next session" block only
  // on page one. The timeline is a single long scroll, so it is always page
  // one here.
  const { nextSession, upcomingSessions } = groupForUpcoming(
    visibleSessionsByMonth,
    ONE,
  );
  const hasTimelineSessions =
    !isEmptyGrouping(nextSession) || !isEmptyGrouping(upcomingSessions);
  // Keep the skeleton while every source is still empty, but render whichever
  // projection resolves first while the other source continues loading.
  const isSessionsLoading =
    !hasTimelineSessions &&
    (isLoading || (nativeStatusApplies && nativeIsLoading));
  // Windowed render over the complete list — see `useIncrementalSessions`.
  // `hasTimelineSessions` stays on the full grouping so the empty-state claims
  // below never read a truncated list as an empty schedule.
  const { sentinelRef, visibleSessions: visibleUpcomingSessions } =
    useIncrementalSessions(upcomingSessions, legacyUserId);
  const isAuthoritativelyEmptySchedule =
    sessions.pages <= ONE &&
    !isSessionsLoading &&
    !(nativeStatusApplies && nativeIsError) &&
    nativeSessions.length === 0 &&
    !hasTimelineSessions;
  const isCountLoadingEmptySchedule =
    isMemberViewer &&
    !skipSessionCount &&
    isSessionCountLoading &&
    sessions.pages <= ONE &&
    !hasTimelineSessions;
  const suppressMemberTimeline =
    isCountLoadingEmptySchedule ||
    (isMemberViewer && isMatchedStage && isAuthoritativelyEmptySchedule);
  const timelinePresentation = isMatchedStage ? 'matched' : 'default';

  const [openCancel, setOpenCancel] = useState<Session | null>();
  const [warningAction, setWarningAction] = useState(
    SessionWarningAction.CANCEL,
  );
  const [selectedSession, setSelectedSession] = useState<Session | null>();
  const [openReschedule, setOpenReschedule] = useState<Session | null>();
  // MEN-4915: a Moments-native coach must reach the coach-native cancel and
  // reschedule dialogs, which `useSessionActions` already owns for the Schedule
  // views. Routing them through the member dialogs instead lands on
  // ProviderAwareSessionAction ("contact support"), because
  // `allowMemberMomentsCancellation` / `enableNativeMoments` are member-only
  // gates that `canUseMemberNativeMomentsCancel` denies to any coach. This hook
  // also owns its own speedbump, so the legacy SessionWarning branches below
  // stay untouched for every other viewer.
  const coachNativeActions = useSessionActions(user.profile?.timezone, {
    onAction: (event) =>
      track(
        event.action === 'cancel'
          ? 'Clicked Cancel Session'
          : 'Clicked Reschedule Session',
        { bookId: event.session.bookId, sessionId: event.session.id },
      ),
    onClose: () => {
      if (canFetchMemberScopeAppointments) {
        memberScopeAppointmentRows.refetch();
      }
    },
  });
  // Native rows carry the member's person id in `session.userId` rather than
  // Rails `User.id`; treat any session with `sourceAppointmentId` as trusted
  // for the redesigned member flow. Legacy sessions still require the Rails
  // id compare so admins and other viewers cannot mis-route.
  const canUseMemberNativeMomentsCancel = (session: Session) => {
    if (isOverview || actingRole === 'coach') return false;
    if (session.sourceAppointmentId) return true;
    return user.id === session.userId;
  };
  // A coach viewing their own Upcoming list, including a coach who also holds
  // admin or support. The native source and API acting-role header keep the
  // list scoped to the authenticated coach identity.
  const canUseCoachNativeMomentsActions = (session: Session) => {
    if (actingRole !== 'coach' || !isCoach(user)) return false;
    if (isOverview) {
      return (
        canFetchMemberScopeAppointments &&
        !memberScopeRowsReadOnly &&
        Boolean(session.sourceAppointmentId)
      );
    }
    return Boolean(session.sourceAppointmentId);
  };
  // Native-capable members on a Moments session use the redesigned flows.
  // Other viewers still use this predicate to select the provider-aware mode.
  const isNativeMomentsSession = (session: Session) =>
    canUseMemberNativeMomentsCancel(session) && isMomentsSession(session);

  const onCloseWarning = () => setSelectedSession(null);

  const handleOnReschedule = (session: Session) => {
    if (canUseCoachNativeMomentsActions(session)) {
      coachNativeActions.openReschedule(session);
      return;
    }

    // Every canonical Moments flow owns its warning policy. Viewer authority
    // is preserved later by `enableNativeMoments` on RescheduleSession.
    if (session.sourceAppointmentId || isMomentsSession(session)) {
      track('Clicked Reschedule Session', {
        bookId: session.bookId,
        sessionId: session.id,
      });
      setOpenReschedule(session);
      return;
    }

    if (isInNumberOfDays(session.startedAt, TWO)) {
      track('Shown Reschedule Speedbump', {
        bookId: session.bookId,
        sessionId: session.id,
      });
      setWarningAction(SessionWarningAction.RESCHEDULE);
      setSelectedSession(session);
      return;
    }

    track('Clicked Reschedule Session', {
      bookId: session.bookId,
      sessionId: session.id,
    });

    setOpenReschedule(session);
  };

  const handleOnCancel = (session: Session) => {
    if (canUseCoachNativeMomentsActions(session)) {
      // openCancel is async only for the legacy intent write; the coach-native
      // branch resolves synchronously and errors are surfaced by the dialog.
      coachNativeActions.openCancel(session).catch(() => undefined);
      return;
    }

    if (isNativeMomentsSession(session)) {
      track('Clicked Cancel Session', {
        bookId: session.bookId,
        sessionId: session.id,
      });
      setOpenCancel(session);
      return;
    }

    if (isInNumberOfDays(session.startedAt, TWO)) {
      track('Shown Cancel Speedbump', {
        bookId: session.bookId,
        sessionId: session.id,
      });
      setWarningAction(SessionWarningAction.CANCEL);
      setSelectedSession(session);
      return;
    }

    track('Clicked Cancel Session', {
      bookId: session.bookId,
      sessionId: session.id,
    });

    setOpenCancel(session);
  };

  const onCloseModal = () => {
    setOpenCancel(null);
    setOpenReschedule(null);
    refetch();
  };

  const onCancelled = () => {
    setShowCancelConfirmation(true);
    navigate('.', { replace: true, state: null });
  };
  const onCloseCancelConfirmation = () => setShowCancelConfirmation(false);

  if (!isLoading && isMemberViewer && user.coaches.length === 0)
    return <MatchingCoach />;

  return (
    <>
      <ConfirmationSnackbar
        autoHideDuration={4000}
        message="Session cancelled."
        open={showCancelConfirmation}
        onClose={onCloseCancelConfirmation}
      />
      <ConfirmationSnackbar
        message={pendingAppointmentMessage ?? ''}
        open={Boolean(pendingAppointmentMessage)}
        onClose={() => setPendingAppointmentMessage(null)}
      />
      <Snackbar
        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
        open={Boolean(pendingAppointmentRejectionMessage)}
      >
        <Alert
          severity="error"
          onClose={() => setPendingAppointmentRejectionMessage(null)}
        >
          {pendingAppointmentRejectionMessage}
        </Alert>
      </Snackbar>
      <Snackbar
        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
        open={Boolean(pendingAppointmentReconciliationMessage)}
      >
        <Alert
          severity="warning"
          onClose={() => setPendingAppointmentReconciliationMessage(null)}
        >
          {pendingAppointmentReconciliationMessage}
        </Alert>
      </Snackbar>
      <>
        {isMemberViewer && (
          <>
            <MeetCoach visible={isMatchedStage} />
            <ClientCoaches
              coaches={user.coaches}
              isLoading={isLoading}
              presentation={timelinePresentation}
              refetch={refetch}
              user={user}
            />
          </>
        )}

        {!suppressMemberTimeline && <SessionsTabs />}

        {!suppressMemberTimeline && (
          <SessionList
            memberRows={isMemberViewer}
            presentation={timelinePresentation}
            sessions={nextSession}
            type={SessionListType.Next}
            hideIfEmpty
            onCancel={handleOnCancel}
            onReschedule={handleOnReschedule}
          />
        )}
      </>

      {!suppressMemberTimeline && (
        <SessionList
          // Hide the built-in "No upcoming sessions" text when the native
          // fetch failed: our banner below owns that message so we do not
          // stack two conflicting empty-state UIs.
          hideIfEmpty={
            isLoading ||
            (nativeStatusApplies && (nativeIsError || nativeIsLoading))
          }
          loading={isSessionsLoading}
          memberRows={isMemberViewer}
          presentation={timelinePresentation}
          sessions={visibleUpcomingSessions}
          type={SessionListType.Upcoming}
          onCancel={handleOnCancel}
          onReschedule={handleOnReschedule}
        />
      )}

      {!suppressMemberTimeline && (
        <div
          ref={sentinelRef}
          data-testid="sessions-scroll-sentinel"
          aria-hidden
        />
      )}

      {nativeStatusApplies && nativeIsError && (
        <div
          className="border-marigold-300 bg-marigold-50 mt-4 rounded-xl border p-4"
          role="status"
        >
          <Typography className="text-cement-500" variant="body2">
            {nativeErrorBannerCopy}
          </Typography>
        </div>
      )}

      {coachNativeActions.modals}

      {!!selectedSession && (
        <SessionWarning
          action={warningAction}
          close={onCloseWarning}
          session={selectedSession}
          onCancel={setOpenCancel}
          onReschedule={setOpenReschedule}
        />
      )}

      {!!openCancel && (
        <CancelSession
          allowMemberMomentsCancellation={canUseMemberNativeMomentsCancel(
            openCancel,
          )}
          close={onCloseModal}
          session={openCancel}
          onCancelled={onCancelled}
          onRescheduleInstead={() => {
            // Hand off to reschedule for the same session without refetching
            // (no mutation happened). Capture the session before nulling.
            const next = openCancel;
            setOpenCancel(null);
            setOpenReschedule(next);
          }}
        />
      )}
      {!!openReschedule && (
        <RescheduleSession
          close={onCloseModal}
          enableNativeMoments={canUseMemberNativeMomentsCancel(openReschedule)}
          session={openReschedule}
        />
      )}
    </>
  );
};

const Upcoming: FC = () => {
  const { user } = useCurrentUser();
  const { isCoach: isCoachPath, isOverview, selectedUserId } = useView();
  const scope = deriveUpcomingViewScope({
    isCoachPath,
    isOverview,
    selectedMemberId: selectedUserId,
    viewer: user,
  });

  return <ScopedUpcoming key={scope.remountKey} scope={scope} />;
};

export default Upcoming;
